import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useAnalytics } from './utils/analytics';
import { useCanonical } from './utils/useCanonical';
import { FeatureGate } from './utils/featureFlags';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import Footer from './components/Layout/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Dashboard from './pages/Dashboard';
import CreateResume from './pages/CreateResume';
import ResumeDetail from './pages/ResumeDetail';
import PricingPage from './pages/PricingPage';
import ApplicationTracker from './pages/ApplicationTracker';
import AuthCallback from './pages/AuthCallback';
import MagicLinkCallback from './pages/MagicLinkCallback';
import ResumeStudio from './pages/ResumeStudio';
import VerifyEmail from './pages/VerifyEmail';
import Unsubscribe from './pages/Unsubscribe';
import FreeATSCheck from './pages/FreeATSCheck';
import { BlogIndex, BlogPost } from './pages/Blog';
import TemplatesGallery from './pages/TemplatesGallery';
import CoverLetterGenerator from './pages/CoverLetterGenerator';
import LinkedInOptimizer from './pages/LinkedInOptimizer';
import InterviewPrep from './pages/InterviewPrep';
import ReferralProgram from './pages/ReferralProgram';
import JobBoard from './pages/JobBoard';
import ResumeBuilder from './pages/ResumeBuilder';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import FeaturesPage from './pages/FeaturesPage';
import { Toaster } from './components/ui/sonner';
import RoleLanding from './pages/RoleLanding';
import JobRoleLanding from './pages/JobRoleLanding';
import RoleOptimizePage from './pages/RoleOptimizePage';
import VsCompetitor from './pages/VsCompetitor';
import SageLanding from './pages/SageLanding';
import SageWidget from './components/SageWidget';
import ScoreShare from './pages/ScoreShare';
import AdminFunnel from './pages/AdminFunnel';
import AdminBroadcast from './pages/AdminBroadcast';
import AdminUsers from './pages/AdminUsers';
import AdminAcquisition from './pages/AdminAcquisition';
import AdminConversions from './pages/AdminConversions';
import AdminProdReadiness from './pages/AdminProdReadiness';
import AdminStripeStatus from './pages/AdminStripeStatus';
import AdminHealth from './pages/AdminHealth';
import AdminResumeSearch from './pages/AdminResumeSearch';
import AdminLeads from './pages/AdminLeads';
import AdminActivity from './pages/AdminActivity';
import AdminErrors from './pages/AdminErrors';
import AdminIncidents from './pages/AdminIncidents';
import CareerCoach from './pages/CareerCoach';
import NotFound from './pages/NotFound';
import { ROLES } from './data/roleLibrary';
import { useParams } from 'react-router-dom';
import './App.css';

// Dispatcher: /resume/:id matches both SEO role pages and user resume detail pages.
// Public role slugs (e.g. "software-engineer") → RoleLanding.
// Authenticated user resumes (e.g. "resume_449812f1eea0") → ResumeDetail.
// Anything else (typo, broken inbound link) → /free-ats-check (lead-gen fallback).
const ResumeOrRoleDispatcher = () => {
  const { id } = useParams();
  if (ROLES[id]) return <RoleLanding />;
  // Real user resumes are always prefixed with "resume_"
  if (id && id.startsWith('resume_')) {
    return <ProtectedRoute><ResumeDetail /></ProtectedRoute>;
  }
  return <Navigate to="/free-ats-check" replace />;
};

// Shifts main content right when sidebar is visible (authenticated)
const MainContent = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return (
    <main className={`min-h-screen ${isAuthenticated ? 'md:ml-52' : ''}`}>
      {children}
    </main>
  );
};

// AppRouter handles OAuth callback detection and analytics
const AppRouter = () => {
  const location = useLocation();
  
  // Track page views with Google Analytics
  useAnalytics();

  // Set canonical URL for current page
  useCanonical();

  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions with auth checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide global Header/Sidebar on public SEO/viral landing pages that have
  // their own nav and immersive layout (role landings + shared score cards).
  // Resume Studio also goes fullscreen — it has its own sticky header and
  // a dominant iframe preview that needs all horizontal real-estate.
  const roleSlugMatch = location.pathname.match(/^\/resume\/([a-z0-9-]+)$/);
  const isPublicRolePage = roleSlugMatch && ROLES[roleSlugMatch[1]];
  const isStudioPage = /^\/resume\/[^/]+\/studio$/.test(location.pathname);
  const isStandalonePublicPage = /^\/score\//.test(location.pathname) || isPublicRolePage || isStudioPage;

  if (isStandalonePublicPage) {
    return (
      <>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/score/:shareId" element={<ScoreShare />} />
          <Route path="/resume/:id" element={<ResumeOrRoleDispatcher />} />
          <Route path="/resume/:id/studio" element={<ProtectedRoute><ResumeStudio /></ProtectedRoute>} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <Header />
      <Sidebar />
      <Toaster position="top-right" richColors closeButton />
      <MainContent>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/magic" element={<MagicLinkCallback />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/free-ats-check" element={<FreeATSCheck />} />
          <Route path="/jobs/role/:slug" element={<JobRoleLanding />} />
          <Route path="/optimize/:slug" element={<RoleOptimizePage />} />
          <Route path="/vs/:slug" element={<VsCompetitor />} />
          <Route path="/sage" element={<SageLanding />} />
          <Route path="/features" element={<FeaturesPage />} />
          {/* Singular /feature was being indexed by Google as duplicate
              content. Permanent client-side redirect to the canonical
              /features. The prerender script also emits /feature/index.html
              with noindex + canonical=/features so server-side crawlers
              that don't execute JS get the right signal. */}
          <Route path="/feature" element={<Navigate to="/features" replace />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/score/:shareId" element={<ScoreShare />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/new"
            element={
              <ProtectedRoute>
                <CreateResume />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <TemplatesGallery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/:id"
            element={<ResumeOrRoleDispatcher />}
          />
          <Route
            path="/applications"
            element={
              <ProtectedRoute>
                <ApplicationTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cover-letter"
            element={
              <ProtectedRoute>
                <CoverLetterGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/linkedin-optimizer"
            element={
              <ProtectedRoute>
                <LinkedInOptimizer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-prep"
            element={
              <ProtectedRoute>
                <InterviewPrep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referral"
            element={
              <ProtectedRoute>
                <ReferralProgram />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={<JobBoard />}
          />
          <Route
            path="/builder"
            element={
              <ProtectedRoute>
                <ResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach"
            element={
              <ProtectedRoute>
                <FeatureGate
                  flag="career_coach"
                  fallback={<FeatureDisabledPage feature="Career Coach" />}
                >
                  <CareerCoach />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/funnel"
            element={
              <ProtectedRoute>
                <AdminFunnel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/conversions"
            element={
              <ProtectedRoute>
                <AdminConversions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/prod-readiness"
            element={
              <ProtectedRoute>
                <AdminProdReadiness />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stripe-status"
            element={
              <ProtectedRoute>
                <AdminStripeStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/health"
            element={
              <ProtectedRoute>
                <AdminHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <ProtectedRoute>
                <AdminActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/errors"
            element={
              <ProtectedRoute>
                <AdminErrors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/incidents"
            element={
              <ProtectedRoute>
                <AdminIncidents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/broadcast"
            element={
              <ProtectedRoute>
                <AdminBroadcast />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/acquisition"
            element={
              <ProtectedRoute>
                <AdminAcquisition />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/resume-search"
            element={
              <ProtectedRoute>
                <AdminResumeSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <ProtectedRoute>
                <AdminLeads />
              </ProtectedRoute>
            }
          />
          {/* Catch-all: branded 404 instead of a silent route-miss. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </MainContent>
      <Footer />
      {/* Sage floating copilot — only renders for authenticated users on
          in-app routes. Self-suppresses on landing/auth/legal/coach pages. */}
      <SageWidget />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * FeatureDisabledPage — shown when a route is gated off via feature flag.
 * Used as the fallback for <FeatureGate>. Friendly explanation + safe path
 * back to the dashboard so users aren't dead-ended.
 */
function FeatureDisabledPage({ feature }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20 bg-[#f4f5f7]" data-testid="feature-disabled-page">
      <div className="max-w-md text-center">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#dc2626] mb-2">Temporarily unavailable</div>
        <h1 className="font-satoshi text-2xl font-bold text-[#0f1115] mb-3">
          {feature} is paused right now
        </h1>
        <p className="text-[#4b5563] leading-relaxed mb-6">
          We've temporarily turned off this feature while we ship an upgrade.
          Everything else still works — your resumes, applications, and ATS
          tools are unaffected.
        </p>
        <Link to="/dashboard" className="btn-primary inline-flex items-center" data-testid="feature-disabled-go-dashboard">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}

export default App;
